import React, { useState, useMemo } from 'react';
// @ts-ignore
import styles from './ClosedCasesListItem.module.scss';
import TreatmentPatientButton from '../../../../../Components/Treatment/TreatmentPatientButton';
import { useAppContext } from '../../../../../Services/AppContext';
import moment from 'moment';
import UserAvatar from '../../../../../Components/UserAvatar/UserAvatar';
import { Theme } from '../../../../../Enums/Theme.enum';
import Button from '../../../../../Components/Button/Button';
import { joinClasses } from '../../../../../Utils/className.util';
import useTranslation from '../../../../../Hooks/useTranslation';
import Text from '../../../../../Components/Base/Text/Text';
import { connectWithPatient } from '../../../Services/case.service';
import OverlayLoading from '../../../../../Components/OverlayLoading';
import { CaseListItemInterface } from '../CaseListItem';
import EcardFileViewerButton from '../../../../Ecard/Components/EcardFileViewer/EcardFileViewerButton';
import Loading from '../../../../../Components/Loading';
import SavingSpinner from '../../../../../Components/Base/Loading/SavingSpinner';
import usePatientMeta from '../../../../../Components/PatientProfile/Hooks/usePatientMeta';
import CaseMedicalReportButtonList from '../../CaseMedicalReportButtonList/CaseMedicalReportButtonList';
import MarkFileAsPrintedOut from '../../../../Files/Component/MarkFileAsPrintedOut';
import UserAvatarImage from '../../../../../Components/UserAvatar/UserAvatarImage';
import usePatientIdentification from '../../../../Patient/Hooks/usePatientIdentification';
import { User } from '../../../../../graphql/graphql';

const ClosedCasesListItem: React.FC<CaseListItemInterface> = ({ item }) => {
	const { workplaceId, userData, dateFormatMoment } = useAppContext();
	const { identification } = usePatientIdentification(item.patient as User);
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const {
		getPersonActingOnBehalfOfAPatient,
		hasPersonActingOnBehalfOfAPatient,
	} = usePatientMeta(item.patient?.id);

	const handleConnectWithPatient = () => {
		setIsLoading(true);
		try {
			const result = connectWithPatient(
				userData.id,
				item.patient?.id,
				workplaceId,
			);
		} catch (e) {
			setIsLoading(false);
		}
	};

	return (
		<>
			{isLoading && <OverlayLoading />}
			<div className={styles.item}>
				<div className={styles.patient}>
					<div className={styles.patientImageContainer}>
						<UserAvatarImage
							userId={item.patient?.id}
							customClass={styles.patientImage}
							profileImageUrl={item?.patient?.profileImageUrl}
						/>
						{item.isNew && (
							<div className={joinClasses([styles.badge, styles.red])}>
								{t('base.brandNew')}
							</div>
						)}
					</div>
					<div className={styles.patientData}>
						<div className={styles.date}>
							{moment(item.solvedAt).format(dateFormatMoment + ', HH:mm')}
						</div>
						<div className={styles.title}>
							<TreatmentPatientButton
								text={item.patient?.profile?.fullName}
								patientId={item.patient?.id}
								workplaceId={workplaceId}
								loggedUserId={userData.id}
							/>
						</div>
						<div className={styles.text}>
							<span>
								{item.patient?.profile?.age} {t('base.years.many')},{' '}
								{item.patient?.profile?.address?.city}
							</span>
						</div>
						{identification.value && (
							<div className={styles.text}>
								<span>{t(identification.title)}: </span>
								{identification.value}
							</div>
						)}
						{item.patient?.profile?.insuranceCompanyTermItem && (
							<div className={styles.text}>
								<span>
									<Text tx='base.shared.insuranceCompany' inline firstUpper />:
								</span>{' '}
								{item.patient?.profile?.insuranceCompanyTermItem?.value}
							</div>
						)}
						{item.patientParent && (
							<div className={joinClasses([styles.text, styles.mt20])}>
								<span>
									<Text
										tx='base.shared.legalRepresentative'
										inline
										firstUpper
									/>
									:
								</span>
								<div>{item.patientParent?.profile?.fullName}</div>
							</div>
						)}
						{hasPersonActingOnBehalfOfAPatient && (
							<div className={styles.text}>
								<span>
									<Text
										tx='component.patientProfilePersonActingOnBehalfOfAPatient.title'
										inline
										firstUpper
									/>
									:
								</span>
								<div>{getPersonActingOnBehalfOfAPatient()}</div>
							</div>
						)}
					</div>
				</div>
				<div className={styles.content}>
					<div className={styles.title}>{item.title}</div>
					{item?.patient?.userRelations?.map(
						({ id, relationUser, type }, index) => {
							if ([3, 4, 8].includes(type)) {
								return (
									<div className={styles.contentItem} key={id}>
										<div className={styles.lightGrey}>
											<Text
												tx='base.shared.attendingDoctor'
												inline
												firstUpper
											/>
											:
										</div>
										<div>
											{relationUser?.profile?.fullName ?? (
												<Text tx='base.shared.unassigned' inline firstUpper />
											)}
										</div>
									</div>
								);
							}
						},
					)}
					<div className={styles.contentItem}>
						<div className={styles.lightGrey}>
							<Text tx='base.closedByMedic' inline firstUpper />:
						</div>
						<div>
							{item.doctor?.profile?.fullName || (
								<Text tx='base.shared.unassigned' inline firstUpper />
							)}
						</div>
					</div>
				</div>
				<div className={styles.controls}>
					{item?.medicalReports?.length ? (
						<CaseMedicalReportButtonList
							items={item.medicalReports}
							patientId={item.patient.id}
							allowPrintedMarking
						/>
					) : (
						<Text
							tx='content.patient.cases.medicalReport.content1'
							inline
							className={styles.noMedicalReport}
						/>
					)}
					<Button
						title='base.shared.reconnectWithThePatient'
						theme={Theme.GREEN}
						onClick={handleConnectWithPatient}
					/>
				</div>
			</div>
		</>
	);
};
export default ClosedCasesListItem;
